import { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { ArticleContent } from '../organisms/FaqPage/ArticleContent';
import { useSettings } from '../../stateManagement/SettingsContext';
import { Layout } from '../templates';
import { SearchBar } from '../organisms/SearchBar';
import { RelatedArticles } from '../organisms/FaqPage/RelatedArticles';
import { CustomText } from '../atoms';
import { appInsights } from '../../appInsights';
import { JaggedBorderHeader } from '../atoms/JaggedBorderHeader';
import { CustomFaqIcon } from '../atoms/CustomFaqIcon';
import '../styles/hcstyles.css';
// import { trackEvent } from '../../Analytics/ga';
import { JaggedBorderHeaderMobile } from '../atoms/JaggedBorderHeaderMobile';

export const Faq: React.FC = () => {
  const { settings, faqs } = useSettings();
  const navigate = useNavigate();

  const [sectionBannerUrl, setSectionBannerUrl] = useState<string | null>(null);

  // Extract FAQ ID from the URL
  function extractFaqId(url: string): string | undefined {
    const match = url.match(/\/faq\/(\d+)-/);
    return match ? match[1] : undefined;
  }

  const url = window.location.pathname;
  const faqId = extractFaqId(url);

  // Extract language from the URL
  const langMatch = url.match(/\/article\/([^/]+)/);
  const language = langMatch ? langMatch[1] : '';

  const faqToDisplay = useMemo(() => {
    return faqId ? faqs?.find(faq => faq.publish_id === faqId) : null;
  }, [faqId, faqs]);

  const articleContent = faqToDisplay?.translations?.[language]?.body || 'No content available.';
  const articleTitle = faqToDisplay?.translations?.[language]?.title || 'No Title Available';
  const articleId = faqToDisplay?.id || 'No Id Available';
  const publishId = faqToDisplay?.publish_id || 'No Id Available';
  const sectionIdForFaq = faqToDisplay?.section_id || 'No Id Available';
  const sectionName = faqToDisplay?.section_translations?.[language]?.title || 'No Title Available';
  const lastUpdatedAt = faqToDisplay?.updated_at || '';
  const createdAt = faqToDisplay?.created_at || '';

  const updatedDateObj = new Date(lastUpdatedAt);
  const createdDateObj = new Date(createdAt);

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'long', // January
    day: '2-digit', // 09
    year: 'numeric', // 2025
  };

  const formattedUpdatedDate = lastUpdatedAt
    ? updatedDateObj.toLocaleDateString('en-US', dateFormatOptions)
    : '';

  const formattedCreatedDate = createdAt
    ? createdDateObj.toLocaleDateString('en-US', dateFormatOptions)
    : '';
  // .replace(/(\w+)\s(\d{2}),\s(\d{4})/, '$1-$2-$3');

  const notFound = !faqToDisplay || !articleContent;

  const { t }: { t: (key: string, options?: { returnObjects: boolean }) => any } = useTranslation();
  const helperTexts = t('data.helperTexts', { returnObjects: true });
  const categoryHeadings = t('data.categoryHeadings', { returnObjects: true });

  // Set banner image based on section ID
  useEffect(() => {
    const sectionId = faqToDisplay?.section_id;
    if (sectionId) {
      const cdnurl = process.env.REACT_APP_CDN_URL;
      const imageUrl = `${cdnurl}/section-images/${sectionId}.png`;

      fetch(imageUrl, { method: 'HEAD' })
        .then(res => {
          if (res.ok) {
            setSectionBannerUrl(imageUrl);
          } else {
            setSectionBannerUrl(null);
          }
        })
        .catch(() => {
          setSectionBannerUrl(null);
        });
    }
  }, [faqToDisplay]);

  function onClickRelatedArticle(publish_id: string) {
    const faq = faqs?.find(item => item.publish_id === publish_id);
    if (faq) {
      const translation = faq.translations[language];
      if (translation) {
        const formattedTitle = translation.title.toLowerCase().replace(/\s+/g, '-');
        navigate(`/article/${language}/faq/${publish_id}-${formattedTitle}`);
        appInsights.trackEvent({
          name: 'landingPage',
          properties: {
            buttonName: 'relatedArticleTile',
            userName: 'anonymous',
            publishId: publish_id,
            articleTitle: formattedTitle,
            category: faq?.section_translations?.[language]?.title,
            articleId: faq?.id,
            sectionId: faq.section_id,
            language,
          },
        });
        // trackEvent('faq_article_opened', {
        //   publish_id,
        //   formattedTitle,
        // });
      }
    }
  }

  function relatedArticledHref(publish_id: string) {
    const faq = faqs?.find(item => item.publish_id === publish_id);
    const translation = faq?.translations[language];
    if (translation) {
      const formattedTitle = translation.title.toLowerCase().replace(/\s+/g, '-');
      return `/article/${language}/faq/${publish_id}-${formattedTitle}`;
    }
    return '/';
  }

  const onContactUsClick = (): void => {
    // trackEvent('contact_us_button_clicked', {
    //   source: 'faq_article',
    //   article_id: articleId,
    //   publish_Id: publishId,
    //   article_title: articleTitle,
    // });
    navigate(`/contactUs`);
  };

  useEffect(() => {
    if (!notFound) {
      window.scrollTo(0, 0);
    }
  }, [notFound]);

  if (notFound) {
    return (
      <Box
        px={{ base: '2vw', md: '1vw', lg: '15vw' }}
        backgroundColor={settings?.configs.primaryBgColor}
        textAlign="center"
        py={8}
      >
        <Text fontSize="2xl" color={settings?.configs.secondaryTextColor}>
          404 - Article Not Found
        </Text>
        <Text fontSize="lg" color={settings?.configs.secondaryTextColor}>
          The requested article could not be found. Please check the URL or try again later.
        </Text>
      </Box>
    );
  }

  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Layout
      pageBgColor={settings?.configs.primaryBgColor}
      onContactUsClick={onContactUsClick}
      settings={settings}
    >
      <Helmet>
        <title>FAQs</title>
      </Helmet>
      {/* Full-width Search Bar with Dynamic Banner Image */}
      <Box
        width="100vw"
        height={{ base: '35vh', md: '45vh', lg: '50vh' }}
        minHeight={{ base: '280px', md: '320px', lg: '420px' }} // safety
        backgroundImage={`url(${sectionBannerUrl || settings?.configs.bannerImageUrl})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={{ base: '4vw', md: '10vw', lg: '20vw' }}
        position="relative"
        sx={{
          /* ONLY for phone landscape */
          '@media (orientation: landscape) and (max-height: 450px)': {
            height: '45vh',
            minHeight: '240px',
          },
        }}
      >
        <Box
          width="100%"
          zIndex={2}
          mt="150px" // keep original position
          sx={{
            /* override margin only on landscape phones */
            '@media (orientation: landscape) and (max-height: 450px)': {
              mt: '0px',
              transform: 'translateY(10px)', // slight visual nudge, optional
            },
          }}
        >
          <SearchBar
            iconColor={settings?.configs.secondaryAccentColor}
            searchBarTextColor={settings?.configs.searchBarTextColor}
            searchBarDropDownTextColor={settings?.configs.secondaryAccentColor}
            searchBarPx={{
              base: '5vw',
              md: '3vw',
              lg: '1vw',
            }}
            placeholderText={
              categoryHeadings.find(
                (item: Record<string, string>) => 'searchBarPlaceholderText' in item,
              )?.searchBarPlaceholderText || 'Search for Articles'
            }
            placeholderFontSize={{ base: '20px', md: '22px', lg: '24px' }}
            placeholderFontWeight="bold"
          />
        </Box>
        {settings?.configs.showJaggedBorder &&
          (isMobile ? (
            <JaggedBorderHeaderMobile bgColor={settings.configs.primaryBgColor} />
          ) : (
            <JaggedBorderHeader bgColor={settings.configs.primaryBgColor} />
          ))}
      </Box>

      {/* Main Content Section */}
      <Box
        px={{ base: '2vw', md: '1vw', lg: '14vw' }}
        backgroundColor={settings?.configs.primaryBgColor}
        height="100%"
        py={{ base: '2vh', lg: '2vh' }}
        overflow="visible"
        borderTop="1px solid #ffffff"
      >
        <Flex mt={8} gap={8} flexDirection={{ base: 'column', lg: 'row' }}>
          <Box flex="2" shadow="md">
            <ArticleContent
              sectionIdForFaq={sectionIdForFaq}
              title={articleTitle}
              lastUpdated={formattedUpdatedDate}
              createdDate={formattedCreatedDate}
              content={articleContent}
              textColor={settings?.configs.secondaryTextColor}
              bgColor={settings?.configs.secondaryBgColor}
              sectionName={sectionName}
              id={articleId}
              publishId={publishId}
              feedbackText={
                helperTexts.find((item: Record<string, string>) => 'feedbackText' in item)
                  ?.feedbackText
              }
              secondaryAccentColor={settings?.configs.secondaryAccentColor}
              gradientAccentColor={settings?.configs.gradientAccentColor}
            />
          </Box>
          <Box flex="1" backgroundColor={settings?.configs.primaryBgColor}>
            <Box mb={2}>
              <CustomText
                fontWeight="bold"
                variant="h1"
                mb={{ base: '4', md: '6' }}
                display="inline-flex"
              >
                <Box as="span" display="inline-flex" alignItems="center" gap="2">
                  <CustomFaqIcon size="1.7rem" color={settings?.configs.primaryAccentColor} />
                  {
                    categoryHeadings.find(
                      (item: Record<string, string>) => 'relatedArticlesHeading' in item,
                    )?.relatedArticlesHeading
                  }
                </Box>
              </CustomText>
              <Box height="3px" width="60px" background={settings?.configs.gradientAccentColor} />
              <Box
                height="1px"
                width="100%"
                background={settings?.configs.gradientAccentColor}
                mb="6"
              />
            </Box>
            <RelatedArticles
              bgColor={settings?.configs.secondaryBgColor}
              textColor={settings?.configs.secondaryTextColor}
              onClickRelatedArticle={publish_id => {
                onClickRelatedArticle(publish_id);
              }}
              faqId={faqId}
              faqTitle={articleTitle}
              hrefToNavigate={publish_id => relatedArticledHref(publish_id)}
            />
          </Box>
        </Flex>
      </Box>
    </Layout>
  );
};
